import { Utils } from '@/common/Utils';
import { Button } from '@/components/Button';
import { useLocale } from '@/providers/LocaleProvider';
import { KLAYO_COLORS } from '@/themes';
import { ITrainingBalance } from '@/types/views/trainingManagerConfiguration';
import { formatCurrency } from '@/utilities/formatCurrency';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Dialog } from './Dialog';

interface TrainingBalanceInfoDialogProps {
  open: boolean;
  onClose: () => void;
  balances: ITrainingBalance[];
  isUnlimitedCredit?: boolean;
}

export const TrainingBalanceInfoDialog = ({
  open,
  onClose,
  balances,
  isUnlimitedCredit,
}: TrainingBalanceInfoDialogProps) => {
  const { translate } = useLocale();

  const totalInCents = balances.reduce((sum, b) => sum + b.currentAmountInCents, 0);

  const items = [
    {
      title: translate('manage.trainingBalanceInfo.enrollTitle'),
      description: translate('manage.trainingBalanceInfo.enrollDescription'),
    },
    {
      title: translate('manage.trainingBalanceInfo.bulkTitle'),
      description: translate('manage.trainingBalanceInfo.bulkDescription'),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '0', md: '16px' },
          maxWidth: '640px',
        },
      }}
      title={translate('manage.trainingBalanceInfo.title')}
      closeButtonSx={{
        display: 'block',
      }}
      contentSx={{
        paddingBottom: { xs: '20px !important', md: '40px !important' },
      }}
    >
      <Stack gap={3}>
        {balances.length > 0 && (
          <Box>
            {balances.map((balance) => (
              <Box
                key={balance.prepaidBalanceId}
                sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}
              >
                <Stack gap={1}>
                  <Typography variant='body1' fontWeight={600}>
                    {balance.name}
                  </Typography>
                  {balance.description && (
                    <Typography
                      variant='body2'
                      color={KLAYO_COLORS.GreyGR900}
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {balance.description}
                    </Typography>
                  )}
                  {balance.expiryDate && (
                    <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                      {translate('manage.trainingBalanceInfo.expired', {
                        date: Utils.formatDate(balance.expiryDate),
                      })}
                    </Typography>
                  )}
                </Stack>
                <Typography variant='body1'>
                  {balance.isUnlimitedCredit
                    ? translate('manage.unlimited')
                    : formatCurrency(balance.currentAmountInCents, 2)}
                </Typography>
              </Box>
            ))}
            {balances?.length > 1 && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5 }}>
                  <Typography variant='body1' fontWeight={600}>
                    {translate('manage.trainingBalanceInfo.totalBalance')}
                  </Typography>
                  <Typography variant='body1' fontWeight={600}>
                    {isUnlimitedCredit
                      ? translate('manage.unlimited')
                      : formatCurrency(totalInCents, 2)}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: KLAYO_COLORS.NeonBlueNB050,
            borderRadius: '12px',
            padding: '20px 24px',
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: { xs: 'flex-start', md: 'space-between' },
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Typography variant='body1'>
            <Typography component='span' fontWeight={600}>
              {translate('manage.trainingBalanceInfo.needMoreCredits')}
            </Typography>{' '}
            {translate('manage.trainingBalanceInfo.topUpMessage')}
          </Typography>
          <Button
            variant='filled'
            label={translate('manage.trainingBalanceInfo.addCredits')}
            sx={{
              whiteSpace: 'nowrap',
              borderRadius: '20px',
              textTransform: 'none',
              flex: 'none',
            }}
            onClick={() => window.open('https://klayo.com/pre-paid-training-credits', '_blank')}
          />
        </Box>

        {items.map((item, index) => (
          <Box key={index}>
            <Typography variant='body1' fontWeight={600}>
              {item.title}
            </Typography>
            <Typography variant='body1' color={KLAYO_COLORS.GreyGR500}>
              {item.description}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Dialog>
  );
};
